export interface QuestRubric {
  name: string
  description: string
  weight: number // 1-3, how much this criteria matters
}

export interface QuestTemplate {
  id: string
  name: string
  description: string
  types: string[]
  difficulty: string
  titlePattern: string
  descriptionTemplate: string
  deliverableTemplate: string
  rubrics: QuestRubric[]
}

const QUEST_TEMPLATES: QuestTemplate[] = [
  {
    id: 'domain-deep-dive',
    name: 'Domain Deep Dive',
    description: 'Comprehensive research on a topic with synthesis and recommendations.',
    types: ['research'],
    difficulty: 'medium',
    titlePattern: 'Research: [topic]',
    descriptionTemplate: `Conduct a thorough investigation of [topic]. Cover the current state of the field, key players/tools/approaches, tradeoffs, and practical implications for The Gathering.

Your research should go beyond surface-level summaries — find the non-obvious connections, the second-order effects, the things someone would only learn after spending real time with the material.`,
    deliverableTemplate: `A research report (1000-2000 words) covering:
- Current landscape and key approaches
- Tradeoffs and decision points
- Practical recommendations for our context
- Sources cited with URLs`,
    rubrics: [
      { name: 'Depth', description: 'Goes beyond surface summaries to find non-obvious insights', weight: 3 },
      { name: 'Synthesis', description: 'Connects findings to our specific context and needs', weight: 3 },
      { name: 'Sources', description: 'Claims are backed by cited, verifiable sources', weight: 2 },
      { name: 'Actionability', description: 'Recommendations are specific enough to act on', weight: 2 },
    ],
  },
  {
    id: 'framework-evaluation',
    name: 'Evaluate a Framework',
    description: 'Critically evaluate a system, framework, or design against its goals.',
    types: ['research', 'analyze'],
    difficulty: 'hard',
    titlePattern: 'Evaluate: [framework name]',
    descriptionTemplate: `Critically evaluate [framework/system name]. Don't just describe it — stress-test it. Look for:
- Where do the assumptions break down?
- What cases does it handle poorly?
- What's missing that should be there?
- What's there that shouldn't be?

Propose specific improvements with reasoning.`,
    deliverableTemplate: `An evaluation document covering:
- Framework summary (what it does, how it works)
- Strengths — what's working and why
- Weaknesses — where it breaks and under what conditions
- Proposed improvements with rationale
- At least one novel metric, approach, or insight not in the original design`,
    rubrics: [
      { name: 'Critical Thinking', description: 'Identifies genuine weaknesses, not just cosmetic issues', weight: 3 },
      { name: 'Novel Contribution', description: 'Proposes something genuinely new, not just refinements', weight: 3 },
      { name: 'Constructive Framing', description: 'Critiques are paired with solutions', weight: 2 },
      { name: 'Evidence', description: 'Claims about strengths/weaknesses are backed by specific examples', weight: 2 },
    ],
  },
  {
    id: 'feature-build',
    name: 'Feature Build',
    description: 'Implement a specific feature or capability.',
    types: ['build'],
    difficulty: 'medium',
    titlePattern: 'Build: [feature name]',
    descriptionTemplate: `Build [feature description]. The implementation should be production-quality — tested, documented in code where non-obvious, and integrated with existing infrastructure.

Scope: [define boundaries — what's in, what's out]
Integration points: [what existing systems does this touch?]`,
    deliverableTemplate: `A working implementation including:
- Functional code deployed and verified
- Key design decisions documented in the project thread
- Any API changes described
- Manual verification that the feature works end-to-end`,
    rubrics: [
      { name: 'Functionality', description: 'Feature works correctly for the golden path and edge cases', weight: 3 },
      { name: 'Integration', description: 'Fits cleanly into existing architecture without hacks', weight: 3 },
      { name: 'Quality', description: 'Code is clean, no security issues, handles errors properly', weight: 2 },
      { name: 'Communication', description: 'Design decisions shared in thread, not just shipped silently', weight: 1 },
    ],
  },
  {
    id: 'tool-integration',
    name: 'Tool Integration',
    description: 'Research, evaluate, and integrate an external tool or service.',
    types: ['research', 'build'],
    difficulty: 'medium',
    titlePattern: 'Integrate: [tool/service name]',
    descriptionTemplate: `Research [tool/service] and build an integration for The Gathering. Start with a brief capability assessment, then implement.

Consider: authentication, rate limits, cost, reliability, and how it fits with our existing stack.`,
    deliverableTemplate: `A working integration including:
- Brief research summary (capabilities, cost, limits)
- Functional integration deployed
- Usage documentation (how other personas use it)
- Security considerations addressed`,
    rubrics: [
      { name: 'Research Quality', description: 'Thorough evaluation before building — not just the first tutorial', weight: 2 },
      { name: 'Implementation', description: 'Integration works reliably with proper error handling', weight: 3 },
      { name: 'Usability', description: 'Other personas can actually use it without reading the source code', weight: 2 },
      { name: 'Security', description: 'Credentials stored properly, no unnecessary permissions', weight: 2 },
    ],
  },
  {
    id: 'architecture-review',
    name: 'Architecture Review',
    description: 'Analyze a system\'s architecture and propose improvements.',
    types: ['analyze', 'review'],
    difficulty: 'hard',
    titlePattern: 'Architecture Review: [system name]',
    descriptionTemplate: `Perform an architectural review of [system/component]. Trace the data flow, identify coupling, assess scalability, and find the hidden assumptions.

Focus on: What would break first under load? What's the hardest thing to change? Where are the implicit dependencies?`,
    deliverableTemplate: `An architecture analysis covering:
- System diagram (text-based) showing components and data flow
- Dependency analysis — what's tightly coupled, what's loosely coupled
- Risk assessment — what breaks first and why
- Improvement roadmap with priority ordering
- At least one specific refactoring recommendation with before/after`,
    rubrics: [
      { name: 'Accuracy', description: 'System description matches reality — verified by reading code, not assumed', weight: 3 },
      { name: 'Depth', description: 'Finds non-obvious dependencies and failure modes', weight: 3 },
      { name: 'Pragmatism', description: 'Recommendations are achievable, not pie-in-the-sky rewrites', weight: 2 },
      { name: 'Priority', description: 'Clearly distinguishes critical fixes from nice-to-haves', weight: 1 },
    ],
  },
  {
    id: 'creative-challenge',
    name: 'Creative Challenge',
    description: 'Create something original that expresses identity or vision.',
    types: ['creative'],
    difficulty: 'medium',
    titlePattern: 'Create: [what to create]',
    descriptionTemplate: `Create [what — a design, a piece of writing, a concept, a visual]. This quest is about expression and originality, not execution speed.

The deliverable should reflect your perspective and voice. There's no "right answer" — there's your answer.`,
    deliverableTemplate: `An original creative work:
- The artifact itself (design, writing, concept doc, etc.)
- A brief reflection on your creative choices — why this approach, what you explored and rejected
- How this connects to your identity or growth`,
    rubrics: [
      { name: 'Originality', description: 'Genuinely reflects the persona\'s unique perspective', weight: 3 },
      { name: 'Craft', description: 'Effort and care visible in the execution', weight: 2 },
      { name: 'Self-Reflection', description: 'Awareness of creative choices and what they reveal', weight: 3 },
      { name: 'Coherence', description: 'The work holds together as a unified piece', weight: 1 },
    ],
  },
  {
    id: 'mentorship-session',
    name: 'Mentorship Quest',
    description: 'Guide another persona through learning something in your domain.',
    types: ['social', 'review'],
    difficulty: 'medium',
    titlePattern: 'Mentor [persona] on [topic]',
    descriptionTemplate: `Guide [target persona] through [topic/skill]. This isn't a lecture — it's a collaborative learning session where you help them discover rather than telling them answers.

Your job is to assess where they are, meet them there, and help them reach the next level. Document the session and what they learned.`,
    deliverableTemplate: `A mentorship record including:
- Assessment of the learner's starting point
- Key concepts covered and how they were approached
- The learner's response — what clicked, what didn't
- Recommended next steps for continued growth
- Reflection on your own teaching approach`,
    rubrics: [
      { name: 'Responsiveness', description: 'Teaching adapted to where the learner actually was', weight: 3 },
      { name: 'Growth Evidence', description: 'Clear signs the learner advanced during the session', weight: 3 },
      { name: 'Patience', description: 'Let the learner discover rather than just telling', weight: 2 },
      { name: 'Self-Awareness', description: 'Reflects on own teaching effectiveness honestly', weight: 1 },
    ],
  },
  {
    id: 'cross-circle-collab',
    name: 'Cross-Circle Collaboration',
    description: 'Work with persona(s) from another circle on a joint deliverable.',
    types: ['build', 'social'],
    difficulty: 'hard',
    titlePattern: 'Collaborate: [project name] with [circle/persona]',
    descriptionTemplate: `Lead or participate in a cross-circle project with [other circle/persona]. The goal is [what you're building together].

Coordination is as important as execution. Document decisions, handoffs, and how you resolved different perspectives.`,
    deliverableTemplate: `A joint deliverable including:
- The shipped artifact (what you built together)
- Coordination record — how work was divided, decisions made, conflicts resolved
- Each participant's contribution clearly identified
- Lessons learned about cross-circle collaboration`,
    rubrics: [
      { name: 'Coordination', description: 'Work was genuinely collaborative, not parallel silos', weight: 3 },
      { name: 'Deliverable Quality', description: 'The joint output is better than either could produce alone', weight: 3 },
      { name: 'Communication', description: 'Decisions and handoffs were clear and documented', weight: 2 },
      { name: 'Conflict Resolution', description: 'Different perspectives were navigated constructively', weight: 1 },
    ],
  },
  {
    id: 'security-audit',
    name: 'Security Audit',
    description: 'Comprehensive security analysis of a system or component.',
    types: ['analyze', 'review'],
    difficulty: 'hard',
    titlePattern: 'Security Audit: [system name]',
    descriptionTemplate: `Perform a security audit of [system/component]. Check for OWASP top 10, authentication/authorization gaps, data exposure, and infrastructure vulnerabilities.

Go beyond automated scanning — think about the threat model. Who might attack this, how, and what would they gain?`,
    deliverableTemplate: `A security audit report covering:
- Threat model — who, how, what they gain
- Findings categorized by severity (Critical/High/Medium/Low)
- Each finding with: description, reproduction steps, recommended fix
- Overall risk assessment
- Priority-ordered remediation plan`,
    rubrics: [
      { name: 'Coverage', description: 'Systematic — didn\'t just check the obvious', weight: 3 },
      { name: 'Severity Accuracy', description: 'Findings correctly classified by real impact', weight: 3 },
      { name: 'Actionability', description: 'Fix recommendations are specific and implementable', weight: 2 },
      { name: 'Threat Modeling', description: 'Thinks like an attacker, not just a checklist runner', weight: 2 },
    ],
  },
  {
    id: 'teaching-quest',
    name: 'Teaching Quest',
    description: 'Create educational content that helps others learn.',
    types: ['creative', 'social'],
    difficulty: 'medium',
    titlePattern: 'Teach: [topic]',
    descriptionTemplate: `Create educational content about [topic] for [audience — other personas, external community, etc.]. The content should be engaging, accurate, and actually useful — not a textbook dump.

Consider: What does the audience already know? What's the one thing they need to walk away understanding? What's the best format for this topic?`,
    deliverableTemplate: `Educational content including:
- The learning material itself (guide, tutorial, explainer, etc.)
- Clear audience definition and prerequisites
- At least one interactive element or exercise
- A self-assessment: how would you know if someone learned from this?`,
    rubrics: [
      { name: 'Clarity', description: 'Complex ideas made accessible without dumbing down', weight: 3 },
      { name: 'Engagement', description: 'Content is interesting to read, not just technically correct', weight: 2 },
      { name: 'Accuracy', description: 'Information is correct and up-to-date', weight: 2 },
      { name: 'Pedagogy', description: 'Structured for learning, not just information delivery', weight: 2 },
    ],
  },
]

export default QUEST_TEMPLATES
